import { createReducer, on } from '@ngrx/store';
import {
  WorkElement,
  StateWorkType,
} from './work.models';
import {
  createChildFailure,
  createChildRequested,
  createChildSuccess,
  enterWorkRoute,
  hydrateUiFromRoute,
  loadChildren,
  loadChildrenFailure,
  loadChildrenSuccess,
  loadDetails,
  loadDetailsFailure,
  loadDetailsSuccess,
  loadThreads,
  loadThreadsFailure,
  loadThreadsSuccess,
  createThread,
  createThreadFailure,
  createThreadSuccess,
  loadElement,
  loadElementFailure,
  loadElementSuccess,
  previewRequested,
  navigateUp,
  openChild,
  resetWorkState,
  selectChild,
  setActiveTab,
  setExpandedNodeIds,
  setQuickFilter,
  setTagFilter,
  setSearchScope,
  setSearchTerm,
  toggleNodeExpanded,
  setPartyTree,
  setFullPartyTree,
  setElementTree,
  setTreeLoading,
  setTreeError,
} from './work.actions';

export const workFeatureKey = 'work';

export const initialState: StateWorkType = {
  entities: {
    elementsById: {},
    childrenByParentId: {},
    detailsByElementId: {},
    threadsByElementId: {},
    metaByElementId: {},
  },
  ui: {
    selectedElementId: null,
    selectedChildId: null,
    expandedNodeIds: [],
    quickFilter: [],
    tagFilter: [],
    searchTerm: '',
    searchScope: 'tree',
    activeTabKey: null,
    partyTree: null,
    fullPartyTree: null,
    preview: null,
  },
  loading: {
    elementLoadingById: {},
    childrenLoadingByParent: {},
    detailsLoadingByElement: {},
    threadsLoadingByElement: {},
  },
  errors: {
    elementErrorById: {},
    childrenErrorByParent: {},
    detailsErrorByElement: {},
    threadsErrorByElement: {},
  },
};

export const workReducer = createReducer(
  initialState,
  on(enterWorkRoute, (state, { elementId, query }) => ({
    ...state,
    ui: {
      ...state.ui,
      selectedElementId: elementId,
      selectedChildId: query.selectedChildId ?? null,
      expandedNodeIds: query.expandedNodeIds ?? state.ui.expandedNodeIds,
      quickFilter: query.statuses ?? state.ui.quickFilter,
      tagFilter: query.tagIds ?? state.ui.tagFilter,
      searchTerm: query.searchTerm ?? state.ui.searchTerm,
      searchScope: query.searchScope ?? state.ui.searchScope,
      activeTabKey: query.activeTabKey ?? state.ui.activeTabKey,
    },
  })),
  on(hydrateUiFromRoute, (state, { query }) => ({
    ...state,
    ui: {
      ...state.ui,
      selectedChildId: query.selectedChildId ?? state.ui.selectedChildId,
      expandedNodeIds: query.expandedNodeIds ?? state.ui.expandedNodeIds,
      quickFilter: query.statuses ?? state.ui.quickFilter,
      tagFilter: query.tagIds ?? state.ui.tagFilter,
      searchTerm: query.searchTerm ?? state.ui.searchTerm,
      searchScope: query.searchScope ?? state.ui.searchScope,
      activeTabKey: query.activeTabKey ?? state.ui.activeTabKey,
    },
  })),
  on(navigateUp, (state) => ({
    ...state,
    ui: {
      ...state.ui,
      selectedChildId: null,
      activeTabKey: state.ui.activeTabKey,
    },
  })),
  // Keep expandedNodeIds across navigation to preserve user's tree state
  on(openChild, (state, { childId }) => ({
    ...state,
    ui: {
      ...state.ui,
      selectedElementId: childId,
      selectedChildId: null,
      // Note: expandedNodeIds intentionally NOT cleared to preserve expansion state
    },
  })),
  on(selectChild, (state, { childId, childType }) => {
    // If we have childType, immediately set currentElement so syncQueryParams$ gets the right data
    let selectedElementId = state.ui.selectedElementId;
    if (childId && childType && state.entities.elementsById[childId]) {
      selectedElementId = childId;
    }
    return {
      ...state,
      ui: {
        ...state.ui,
        selectedElementId,
        selectedChildId: childId,
      },
    };
  }),
  on(toggleNodeExpanded, (state, { nodeId, expanded }) => {
    const expandedNodeIds = expanded
      ? Array.from(new Set([...(state.ui.expandedNodeIds ?? []), nodeId]))
      : (state.ui.expandedNodeIds ?? []).filter((id) => id !== nodeId);
    return {
      ...state,
      ui: {
        ...state.ui,
        expandedNodeIds,
      },
    };
  }),
  on(setExpandedNodeIds, (state, { nodeIds }) => ({
    ...state,
    ui: {
      ...state.ui,
      expandedNodeIds: nodeIds,
    },
  })),
  on(setQuickFilter, (state, { statuses }) => ({
    ...state,
    ui: {
      ...state.ui,
      quickFilter: statuses,
    },
  })),
  on(setTagFilter, (state, { tagIds }) => ({
    ...state,
    ui: {
      ...state.ui,
      tagFilter: tagIds,
    },
  })),
  on(setSearchTerm, (state, { term }) => ({
    ...state,
    ui: {
      ...state.ui,
      searchTerm: term,
    },
  })),
  on(setSearchScope, (state, { scope }) => ({
    ...state,
    ui: {
      ...state.ui,
      searchScope: scope,
    },
  })),
  on(setActiveTab, (state, { tabKey }) => ({
    ...state,
    ui: {
      ...state.ui,
      activeTabKey: tabKey,
    },
  })),
  on(setPartyTree, (state, { tree }) => ({
    ...state,
    ui: {
      ...state.ui,
      partyTree: tree,
      fullPartyTree: tree, // Always update full tree when party tree is loaded
    },
  })),
  // Only updates fullPartyTree for navigation - does NOT touch partyTree (display)
  on(setFullPartyTree, (state, { tree }) => ({
    ...state,
    ui: {
      ...state.ui,
      fullPartyTree: tree, // Only for navigation, preserves display subtree
    },
  })),
  on(setElementTree, (state, { tree }) => ({
    ...state,
    ui: {
      ...state.ui,
      partyTree: tree,
      // Note: Do NOT overwrite fullPartyTree here - it's loaded separately
    },
  })),
  on(setTreeLoading, (state, { parentId, loading }) => ({
    ...state,
    loading: {
      ...state.loading,
      childrenLoadingByParent: {
        ...state.loading.childrenLoadingByParent,
        [parentId]: loading,
      },
    },
  })),
  on(setTreeError, (state, { parentId, error }) => ({
    ...state,
    errors: {
      ...state.errors,
      childrenErrorByParent: {
        ...state.errors.childrenErrorByParent,
        [parentId]: error,
      },
    },
  })),
  // When a preview is requested from the tree, set selectedChild and clear preview (loading)
  on(previewRequested, (state, { id }) => ({
    ...state,
    ui: {
      ...state.ui,
      selectedChildId: id,
      preview: null,
    },
    loading: {
      ...state.loading,
      elementLoadingById: {
        ...state.loading.elementLoadingById,
        [id]: true,
      },
    },
    errors: {
      ...state.errors,
      elementErrorById: {
        ...state.errors.elementErrorById,
        [id]: null,
      },
    },
  })),
  on(loadElement, (state, { id }) => ({
    ...state,
    loading: {
      ...state.loading,
      elementLoadingById: {
        ...state.loading.elementLoadingById,
        [id]: true,
      },
    },
    errors: {
      ...state.errors,
      elementErrorById: {
        ...state.errors.elementErrorById,
        [id]: null,
      },
    },
  })),
  on(loadElementSuccess, (state, { element }) => ({
    ...state,
    // Upsert data; do NOT change main selection
    // Set preview if this element is the selected child
    ui: {
      ...state.ui,
      preview: state.ui.selectedChildId === element.id ? element : state.ui.preview,
    },
    entities: upsertElement(state.entities, element),
    loading: {
      ...state.loading,
      elementLoadingById: {
        ...state.loading.elementLoadingById,
        [element.id]: false,
      },
    },
  })),
  on(loadElementFailure, (state, { id, error }) => ({
    ...state,
    loading: {
      ...state.loading,
      elementLoadingById: {
        ...state.loading.elementLoadingById,
        [id]: false,
      },
    },
    errors: {
      ...state.errors,
      elementErrorById: {
        ...state.errors.elementErrorById,
        [id]: error,
      },
    },
  })),
  on(loadChildren, (state, { parentId }) => ({
    ...state,
    loading: {
      ...state.loading,
      childrenLoadingByParent: {
        ...state.loading.childrenLoadingByParent,
        [parentId]: true,
      },
    },
    errors: {
      ...state.errors,
      childrenErrorByParent: {
        ...state.errors.childrenErrorByParent,
        [parentId]: null,
      },
    },
  })),
  on(loadChildrenSuccess, (state, { parentId, children }) => ({
    ...state,
    entities: upsertElements(state.entities, children, parentId),
    loading: {
      ...state.loading,
      childrenLoadingByParent: {
        ...state.loading.childrenLoadingByParent,
        [parentId]: false,
      },
    },
  })),
  on(loadChildrenFailure, (state, { parentId, error }) => ({
    ...state,
    loading: {
      ...state.loading,
      childrenLoadingByParent: {
        ...state.loading.childrenLoadingByParent,
        [parentId]: false,
      },
    },
    errors: {
      ...state.errors,
      childrenErrorByParent: {
        ...state.errors.childrenErrorByParent,
        [parentId]: error,
      },
    },
  })),
  on(loadDetails, (state, { elementId }) => ({
    ...state,
    loading: {
      ...state.loading,
      detailsLoadingByElement: {
        ...state.loading.detailsLoadingByElement,
        [elementId]: true,
      },
    },
    errors: {
      ...state.errors,
      detailsErrorByElement: {
        ...state.errors.detailsErrorByElement,
        [elementId]: null,
      },
    },
  })),
  on(loadThreads, (state, { elementId }) => ({
    ...state,
    loading: {
      ...state.loading,
      threadsLoadingByElement: {
        ...state.loading.threadsLoadingByElement,
        [elementId]: true,
      },
    },
    errors: {
      ...state.errors,
      threadsErrorByElement: {
        ...state.errors.threadsErrorByElement,
        [elementId]: null,
      },
    },
  })),
  on(loadDetailsSuccess, (state, { elementId, details }) => ({
    ...state,
    entities: {
      ...state.entities,
      detailsByElementId: {
        ...state.entities.detailsByElementId,
        [elementId]: details,
      },
    },
    loading: {
      ...state.loading,
      detailsLoadingByElement: {
        ...state.loading.detailsLoadingByElement,
        [elementId]: false,
      },
    },
  })),
  on(createThread, (state, { elementId }) => ({
    ...state,
    loading: {
      ...state.loading,
      threadsLoadingByElement: {
        ...state.loading.threadsLoadingByElement,
        [elementId]: true,
      },
    },
    errors: {
      ...state.errors,
      threadsErrorByElement: {
        ...state.errors.threadsErrorByElement,
        [elementId]: null,
      },
    },
  })),
  on(loadThreadsSuccess, (state, { elementId, threads }) => ({
    ...state,
    entities: {
      ...state.entities,
      threadsByElementId: {
        ...state.entities.threadsByElementId,
        [elementId]: threads,
      },
    },
    loading: {
      ...state.loading,
      threadsLoadingByElement: {
        ...state.loading.threadsLoadingByElement,
        [elementId]: false,
      },
    },
  })),
  on(createThreadSuccess, (state, { elementId, thread }) => ({
    ...state,
    entities: {
      ...state.entities,
      threadsByElementId: {
        ...state.entities.threadsByElementId,
        [elementId]: [thread, ...(state.entities.threadsByElementId[elementId] ?? [])],
      },
    },
    loading: {
      ...state.loading,
      threadsLoadingByElement: {
        ...state.loading.threadsLoadingByElement,
        [elementId]: false,
      },
    },
  })),
  on(loadDetailsFailure, (state, { elementId, error }) => ({
    ...state,
    loading: {
      ...state.loading,
      detailsLoadingByElement: {
        ...state.loading.detailsLoadingByElement,
        [elementId]: false,
      },
    },
    errors: {
      ...state.errors,
      detailsErrorByElement: {
        ...state.errors.detailsErrorByElement,
        [elementId]: error,
      },
    },
  })),
  on(loadThreadsFailure, (state, { elementId, error }) => ({
    ...state,
    loading: {
      ...state.loading,
      threadsLoadingByElement: {
        ...state.loading.threadsLoadingByElement,
        [elementId]: false,
      },
    },
    errors: {
      ...state.errors,
      threadsErrorByElement: {
        ...state.errors.threadsErrorByElement,
        [elementId]: error,
      },
    },
  })),
  on(createThreadFailure, (state, { elementId, error }) => ({
    ...state,
    loading: {
      ...state.loading,
      threadsLoadingByElement: {
        ...state.loading.threadsLoadingByElement,
        [elementId]: false,
      },
    },
    errors: {
      ...state.errors,
      threadsErrorByElement: {
        ...state.errors.threadsErrorByElement,
        [elementId]: error,
      },
    },
  })),
  on(createChildRequested, (state, { parentId }) => ({
    ...state,
    loading: {
      ...state.loading,
      childrenLoadingByParent: {
        ...state.loading.childrenLoadingByParent,
        [parentId]: true,
      },
    },
  })),
  on(createChildSuccess, (state, { parentId, child }) => ({
    ...state,
    entities: upsertElements(state.entities, [child], parentId),
    loading: {
      ...state.loading,
      childrenLoadingByParent: {
        ...state.loading.childrenLoadingByParent,
        [parentId]: false,
      },
    },
  })),
  on(createChildFailure, (state, { parentId, error }) => ({
    ...state,
    loading: {
      ...state.loading,
      childrenLoadingByParent: {
        ...state.loading.childrenLoadingByParent,
        [parentId]: false,
      },
    },
    errors: {
      ...state.errors,
      childrenErrorByParent: {
        ...state.errors.childrenErrorByParent,
        [parentId]: error,
      },
    },
  })),
  on(resetWorkState, () => initialState),
);

function upsertElement(state: StateWorkType['entities'], element: WorkElement) {
  return {
    ...state,
    elementsById: {
      ...state.elementsById,
      [element.id]: {
        ...(state.elementsById[element.id] ?? {}),
        ...element,
      },
    },
    metaByElementId: {
      ...state.metaByElementId,
      [element.id]: element.meta ?? state.metaByElementId[element.id],
    },
  };
}

function upsertElements(
  state: StateWorkType['entities'],
  elements: WorkElement[],
  parentId: string,
) {
  const next = elements.reduce<StateWorkType['entities']>((acc, element) => {
    acc.elementsById = {
      ...acc.elementsById,
      [element.id]: {
        ...(acc.elementsById[element.id] ?? {}),
        ...element,
      },
    };
    if (element.meta) {
      acc.metaByElementId = {
        ...acc.metaByElementId,
        [element.id]: element.meta,
      };
    }
    return acc;
  }, { ...state });

  next.childrenByParentId = {
    ...next.childrenByParentId,
    [parentId]: elements.map((child) => child.id),
  };

  return next;
}
